import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  IconCheckmark,
  IconDisabled,
  List,
  Notification,
  StatusCard,
} from 'shared/components';
import { displayValue, formatDate, getLicenseStatus } from 'shared/helpers';
import { fetchLicensesByLicenseUser } from '../../licenses/actions';

const PAGE_SIZE = 10;

const LicenseUserLicenses = ({ licenseUserId }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [licenses, setLicenses] = useState([]);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const getLicenses = useCallback(
    async (page, sort) => {
      setIsLoading(true);
      try {
        const response = await fetchLicensesByLicenseUser(
          licenseUserId,
          sort,
          page,
          PAGE_SIZE,
        );
        setLicenses(get(response, 'data.results', []));
        setCount(get(response, 'data.count', 0));
      } catch (error) {
        Notification(
          'error',
          t('Error occurred'),
          t('There was an error while getting licenses'),
        );
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [licenseUserId],
  );

  useEffect(() => {
    if (licenseUserId) {
      getLicenses(currentPage, currentSort);
    }
  }, [licenseUserId, currentPage, currentSort, getLicenses, retryToken]);

  const handleSortChange = (newSort) => setCurrentSort(newSort);

  const handleRowClick = (row) => {
    const licenseId = get(row, 'id');
    if (licenseId) {
      history.push(`/licenses/${licenseId}`);
    }
  };

  const handleRetry = () => {
    setRetryToken((token) => token + 1);
    setError(false);
  };

  const columns = [
    {
      Header: t('Created on'),
      accessor: 'created_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('License key'),
      id: 'license_key',
      accessor: (row) =>
        get(row, 'license_key') ?? get(row, 'licenseKey') ?? null,
      Cell: (cellData) => displayValue(cellData.value),
      width: 220,
    },
    {
      Header: t('Product'),
      accessor: 'product.product_name',
      Cell: (cellData) => displayValue(cellData.value),
      sortable: false,
    },
    {
      Header: t('License Type'),
      accessor: 'license_type',
      Cell: (rowData) => {
        const isBundle = get(rowData, 'original.is_bundle');
        if (isBundle) return '-';
        return displayValue(t(rowData.value));
      },
      width: 120,
      sortable: false,
    },
    {
      Header: t('Expiration Date'),
      id: 'expiration_date',
      accessor: (row) =>
        get(row, 'expiration_date') ??
        get(row, 'expires_at') ??
        get(row, 'valid_until') ??
        get(row, 'maintenance_period') ??
        null,
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 160,
      sortable: false,
    },
    {
      Header: t('Times activated'),
      accessor: 'times_activated',
      className: 'text-center',
      headerClassName: 'text-center',
      Cell: (rowData) => {
        const isBundle = get(rowData, 'original.is_bundle');
        if (isBundle) return '-';
        return displayValue(rowData.value, 0);
      },
      width: 140,
    },
    {
      Header: t('Is trial'),
      accessor: 'is_trial',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (cellData) =>
        cellData.value ? (
          <IconCheckmark color="#10ac84" height="14px" />
        ) : (
          <IconDisabled color="#aaa" height="14px" />
        ),
      width: 90,
      sortable: false,
    },
    {
      Header: t('Status'),
      id: 'status',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (rowData) => {
        const isBundle = get(rowData, 'original.is_bundle');
        if (isBundle) return '-';
        const { status, message } = getLicenseStatus(rowData, t);
        return <StatusCard status={status} text={message} />;
      },
      width: 150,
      sortable: false,
    },
  ];
  if (isLoading) {
    return <ContentLoader text={t('Loading licenses')} />;
  }

  if (error) {
    return (
      <div className="LicenseUserDetails-error">
        <p>{t('Unable to load licenses for this user')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!licenses.length) {
    return (
      <div className="LicenseUserDetails-empty">
        {t('No licenses for this user')}
      </div>
    );
  }

  return (
    <div className="LicenseUserDetails-list">
      <List
        data={licenses}
        columns={columns}
        minRows={2}
        loading={isLoading}
        manual
        defaultSorted={currentSort}
        onSortedChange={handleSortChange}
        page={currentPage}
        pages={Math.ceil(count / PAGE_SIZE)}
        showPagination={count > PAGE_SIZE}
        onPageChange={(page) => setCurrentPage(page)}
        clickable
        getTrProps={(state, rowInfo) => ({
          onClick: () => handleRowClick(rowInfo.original),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
};

LicenseUserLicenses.propTypes = {
  licenseUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default LicenseUserLicenses;
